import React, { useState, useEffect } from "react";
import {
  Button,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import Leaves from "../../../HR/Requests/requestedLeaves";
import {
  fmtDate,
  fmtTime,
  fmtDuration,
} from "../../../HR/AttendanceEditor/attendanceTime";
import "./managerInfo.css";

// Same line as a normal checkout: time beyond 9h 25m is overtime.
const OVERTIME_GRACE_MS = (9 * 60 + 25) * 60 * 1000;
// "Wed, Sep 23" — the list only spans the last 62 days, so the year is noise. Stored times
// are PKT wall-clock digits written as UTC, hence timeZone "UTC".
const OT_DAY_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const otDay = (iso) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "—" : OT_DAY_FMT.format(new Date(t));
};
const PENDING_SOURCE = {
  "added-by-hr": "Added by HR",
  "edited-by-hr": "Edited by HR",
};

const ManagerInfo = (props) => {
  const user = props.user;
  const Employee = props.Employee;

  const [leademployees, setleadEmployees] = useState([]);
  const [selectedleadEmployee, setSelectedleadEmployee] = useState(null);
  const [selectedUnleadEmployee, setSelectedUnleadEmployee] = useState(null);
  const [projectData, setProjectData] = useState([]); // Initialize as an empty array
  const [reportType, setReportType] = useState("employeesOfManager");
  const [openDialog, setOpenDialog] = useState(false);
  const [openUnassignDialog, setOpenUnassignDialog] = useState(false);
  const [unleadEmployees, setunleadEmployees] = useState([]);
  const [selectedReportDetail, setSelectedReportDetail] = useState("");
  const [selectedOvertimeNote, setSelectedOvertimeNote] = useState("");
  const [openD, setOpenD] = useState(false);

  // Team-member details popup
  const [empDialogOpen, setEmpDialogOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);

  const [requestsPage, setRequestsPage] = useState(1); // Pagination starts from 1
  const [mastermindRowsPerPage, setMastermindRowsPerPage] = useState(10);
  const [mastermindTotalCount, setMastermindTotalCount] = useState(0);

  const [progressPage, setProgressPage] = useState(1); // Pagination starts from 1
  const [progressRowsPerPage, setProgressRowsPerPage] = useState(10);
  const [progressTotalCount, setProgressTotalCount] = useState(0);

  // Progress Report filters/sort (default: no date filter, newest first)
  const [progressFromInput, setProgressFromInput] = useState("");
  const [progressToInput, setProgressToInput] = useState("");
  const [progressFrom, setProgressFrom] = useState("");
  const [progressTo, setProgressTo] = useState("");
  const [progressSort, setProgressSort] = useState("date-desc");
  const [progressEmployeeInput, setProgressEmployeeInput] = useState("");
  const [progressEmployee, setProgressEmployee] = useState("");

  // Alert states for MUI alerts
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Sales Rep status of the viewed employee (kept in sync with the prop).
  const [isSalesRep, setIsSalesRep] = useState(Employee?.salesRep === "yes");

  // Pending overtime: every day of this team waiting on an overtime decision. The Progress
  // Report table only offers Approve/Reject when a report was filed right after the
  // check-out, so days HR fixed or added (and filled-in missed check-outs) are decided here.
  const [pendingOt, setPendingOt] = useState([]);
  const [pendingOtLoading, setPendingOtLoading] = useState(false);
  const [pendingOtError, setPendingOtError] = useState("");
  const [pendingOtBusy, setPendingOtBusy] = useState(null);
  useEffect(() => {
    setIsSalesRep(Employee?.salesRep === "yes");
  }, [Employee?.email, Employee?.salesRep]);

  const mastermindTotalPages = Math.ceil(
    mastermindTotalCount / mastermindRowsPerPage
  );
  const progressTotalPages = Math.ceil(
    progressTotalCount / progressRowsPerPage
  );

  const handleProgressPageChange = (event, newPage) => {
    setProgressPage(newPage);
  };

  const handleProgressDropdownChange = (event) => {
    setProgressPage(event.target.value);
  };

  const applyProgressFilter = () => {
    setProgressFrom(progressFromInput);
    setProgressTo(progressToInput);
    setProgressEmployee(progressEmployeeInput.trim());
    setProgressPage(1);
  };
  const clearProgressFilter = () => {
    setProgressFromInput("");
    setProgressToInput("");
    setProgressFrom("");
    setProgressTo("");
    setProgressEmployeeInput("");
    setProgressEmployee("");
    setProgressPage(1);
  };

  // Alert handler functions
  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await fetch(
          "https://hr-backend-72v1.onrender.com/employees-without-manager"
        );
        const data = await response.json();
        setleadEmployees(data);
      } catch (error) {
        console.error("Error fetching employee data:");
      }
    };

    fetchEmployees();
  }, []);

  const fetchUnderManagerEmployees = async () => {
    const email = Employee.email;
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/employees-under-manager?email=${email}`
      );
      const data = await response.json();
      setunleadEmployees(data);
    } catch (error) {
      console.error("Error fetching employee data:");
    }
  };
  useEffect(() => {
    fetchUnderManagerEmployees();
  }, []);

  const fetchProgressReportData = async (page, rowsPerPage) => {
    try {
      const response = await fetch(
        "https://hr-backend-72v1.onrender.com/getprogressreportdata",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: Employee.email,
            page,
            limit: rowsPerPage,
            fromDate: progressFrom || undefined,
            toDate: progressTo || undefined,
            employeeName: progressEmployee || undefined,
            sortBy: progressSort,
          }),
        }
      );
      const data = await response.json();
      setProjectData(data.progressReports || []);
      setProgressTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching progress report data:");
    }
  };

  useEffect(() => {
    if (reportType === "requests") {
      //   fetchMastermindData(requestsPage, mastermindRowsPerPage);
    } else {
      fetchProgressReportData(progressPage, progressRowsPerPage);
    }
  }, [reportType, requestsPage, progressPage, progressRowsPerPage, progressFrom, progressTo, progressEmployee, progressSort]);

  const fetchPendingOvertime = async () => {
    if (!Employee || !Employee.email) return;
    setPendingOtLoading(true);
    setPendingOtError("");
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/team-pending-overtime?managerEmail=${encodeURIComponent(
          Employee.email
        )}`
      );
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      setPendingOt(Array.isArray(data.records) ? data.records : []);
    } catch (error) {
      setPendingOtError("Couldn't load pending overtime. Please try again.");
    } finally {
      setPendingOtLoading(false);
    }
  };

  // Loaded up front so the tab can show how many days are waiting.
  useEffect(() => {
    if (Employee && Employee.manager === "yes") fetchPendingOvertime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Employee && Employee.email, Employee && Employee.manager]);

  const decidePendingOvertime = async (row, status) => {
    const checkInId = row._id;
    setPendingOtBusy(checkInId);
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/sendReportStatus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The times this row showed — the server refuses (409) if HR changed them since.
        body: JSON.stringify({
          _id: checkInId,
          status,
          expectedCheckIn: row.checkIn || null,
          expectedCheckOut: row.checkOut || null,
        }),
      });
      if (response.ok) {
        setPendingOt((prev) => prev.filter((r) => r._id !== checkInId));
        showAlert(status === "accepted" ? "Overtime approved" : "Overtime rejected", "success");
        // The same day may also sit in the Progress Report table.
        fetchProgressReportData(progressPage, progressRowsPerPage);
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (response.status === 409) {
        await fetchPendingOvertime();
        showAlert(data.message || "This day changed — the list has been refreshed.", "warning");
      } else if (response.status === 403 && /managers or HR only/i.test(data.message || "")) {
        showAlert(
          "Only managers can approve overtime. If you were made a manager recently, log out and log back in.",
          "error"
        );
      } else {
        showAlert(data.message || "Failed to update overtime", "error");
      }
    } catch (error) {
      showAlert("Failed to update overtime", "error");
    } finally {
      setPendingOtBusy(null);
    }
  };

  const handleReportTypeChange = (event, newType) => {
    if (!newType) return; // ignore de-select clicks
    setReportType(newType);
    if (newType === "requests") {
      setRequestsPage();
    } else {
      setProgressPage(1);
    }
    if (newType === "overtime") fetchPendingOvertime();
  };

  const handleOpenDialog = (reportDetail, overtimeNote = "") => {
    setSelectedReportDetail(reportDetail);
    setSelectedOvertimeNote(overtimeNote || "");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReportDetail("");
  };
  const handleOpenUnassignDialog = (reportDetail) => {
    setOpenUnassignDialog(true);
  };

  const handleCloseUnassignDialog = () => {
    setOpenUnassignDialog(false);
  };

  const approveManager = async (projectId) => {
    try {
      const response = await fetch(
        "https://hr-backend-72v1.onrender.com/approve-report-manager",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ projectId }),
        }
      );

      if (response.ok) {
        const updatedProject = await response.json();
        setProjectData((prevData) =>
          prevData.map((project) =>
            project._id === projectId
              ? { ...project, approvedbymanager: true }
              : project
          )
        );
        alert("Project approved by manager");
      } else {
        alert("Failed to approve project detail");
      }
    } catch (error) {
      console.error("Error:");
    }
  };

  const approveHR = async (projectId) => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/approve-report-hr", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId }),
      });

      if (response.ok) {
        const updatedProject = await response.json();
        setProjectData((prevData) =>
          prevData.map((project) =>
            project._id === projectId
              ? { ...project, approvedbyhr: true }
              : project
          )
        );
        alert("Project approved by HR");
      } else {
        alert("Failed to approve project detail");
      }
    } catch (error) {
      console.error("Error:");
    }
  };

  const assignEmployeeToManager = async () => {
    const isValidEmployee = leademployees.some(
      (employee) => employee.email === selectedleadEmployee?.email
    );

    if (!isValidEmployee) {
      alert("Please enter a valid option from the list.");
      return;
    }
    if (!selectedleadEmployee) {
      alert("Please select an employee");
      return;
    }

    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/assign-employee", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          managerEmail: Employee.email,
          managerName: Employee.name,
          employeeName: selectedleadEmployee.name,
          employeeEmail: selectedleadEmployee.email,
        }),
      });

      if (response.ok) {
        alert("Employee assigned to manager successfully");
      } else {
        alert("Failed to assign employee");
      }
    } catch (error) {
      console.error("Error:");
    }
  };
  const unassignEmployee = async () => {
    const isValidEmployee = unleadEmployees.some(
      (employee) => employee.email === selectedUnleadEmployee?.email
    );

    if (!isValidEmployee) {
      alert("Please enter a valid option from the list.");
      return;
    }
    if (!selectedUnleadEmployee) {
      alert("Please select an employee");
      return;
    }

    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/unassign-employee", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          managerEmail: Employee.email,
          employeeName: selectedUnleadEmployee.name,
          employeeEmail: selectedUnleadEmployee.email,
        }),
      });

      if (response.ok) {
        alert("Employee Unassigned");
        fetchUnderManagerEmployees();
        handleCloseUnassignDialog();
      } else {
        alert("Failed to Unassign employee");
      }
    } catch (error) {
      console.error("Error:");
    }
  };

  const promoteToManager = async (email, name) => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/makemanager", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, name }),
      });

      if (response.ok) {
        alert("Employee promoted to Manager");
      } else {
        alert("Failed to promote Employee");
      }
    } catch (error) {
      console.error("Error:");
    }
  };
  const removeFromManager = async (email, name) => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/remove-manager", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, name }),
      });

      if (response.ok) {
        alert("Employee removed from Manager role");
      } else {
        alert("Failed to remove Employee from Manager role");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const makeSalesRep = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/make-salesrep", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: Employee.email }),
      });
      if (response.ok) {
        setIsSalesRep(true);
        showAlert("Employee marked as Sales Rep");
      } else {
        showAlert("Failed to mark as Sales Rep", "error");
      }
    } catch (error) {
      showAlert("Failed to mark as Sales Rep", "error");
    }
  };
  const removeSalesRep = async () => {
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/remove-salesrep", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: Employee.email }),
      });
      if (response.ok) {
        setIsSalesRep(false);
        showAlert("Sales Rep access removed");
      } else {
        showAlert("Failed to remove Sales Rep access", "error");
      }
    } catch (error) {
      showAlert("Failed to remove Sales Rep access", "error");
    }
  };

  // Add handler for sending report status
  const sendReportStatus = async (_id, status) => {
    console.log(_id, status);
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/sendReportStatus", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ _id, status }),
      });
      if (response.ok) {
        setProjectData((prevData) =>
          prevData.map((project) =>
            project._id === _id ? { ...project, status } : project
          )
        );
        await fetchProgressReportData(progressPage, progressRowsPerPage);
        // Keep the PENDING OVERTIME count in step with a decision made from this table.
        fetchPendingOvertime();
        showAlert(
          `Report ${status === "accepted" ? "accepted" : "rejected"}`,
          "success"
        );
      } else {
        showAlert("Failed to update report status", "error");
      }
    } catch (error) {
      console.error("Error updating report status:", error);
    }
  };

  // Open a popup with the clicked team member's personal info. The
  // employees-under-manager list only carries name + email, so fetch the full
  // record from the existing /loginemployee endpoint (no new backend).
  const openEmployeeDetails = async (emp) => {
    setSelectedEmp({ name: emp.name, email: emp.email });
    setEmpDialogOpen(true);
    setEmpLoading(true);
    try {
      const res = await fetch(
        `https://hr-backend-72v1.onrender.com/loginemployee?email=${emp.email}`
      );
      const data = await res.json();
      if (res.ok && data) setSelectedEmp(data);
    } catch (error) {
      // keep the basic name/email already shown
    } finally {
      setEmpLoading(false);
    }
  };

  const employeeDetailFields = (emp) =>
    [
      ["Email", emp?.email],
      ["Phone", emp?.phone],
      ["Position", emp?.pos],
      ["Department", emp?.department],
      ["Team Lead", emp?.leadManager],
      ["CNIC", emp?.cnic],
      ["Date of Birth", emp?.birthday ? String(emp.birthday).slice(0, 10) : ""],
      ["Joining Date", emp?.joining ? String(emp.joining).slice(0, 10) : ""],
      ["Permanent Address", emp?.address],
      ["Present Address", emp?.presentAddress],
    ].filter(([, value]) => value);

  // The profile arrives asynchronously after login. Opening the Manager Tab before it lands
  // used to crash the whole page (Employee is null for that moment) — show a spinner instead.
  // All hooks are above this line, so the early return is safe.
  if (!Employee) {
    return (
      <div className="mgrOtState">
        <CircularProgress size={22} />
      </div>
    );
  }

  const isHrOrExec = user.role === "HR" || user.role === "Executive";
  const isManager = Employee.manager == "yes";
  const initials = (nm) =>
    nm
      ? nm
          .trim()
          .split(/\s+/)
          .map((w) => w[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      : "";

  return (
    <div className="mgrWrap">
      <header className="mgrHeader">
        <div>
          <h1 className="mgrTitle">Manager Tab</h1>
          <p className="mgrSubtitle">
            Manage your direct reports, requests and progress
          </p>
        </div>
        {isManager && (
          <button className="mgrOutlineBtn" onClick={handleOpenUnassignDialog}>
            <PersonRemoveOutlinedIcon sx={{ fontSize: 16 }} />
            Unassign Employee
          </button>
        )}
      </header>

      {/* Sales Rep access - HR/Executive can grant any employee read-only
          access to browse the full Project Showcase (parallel to Make Manager). */}
      {isHrOrExec && (
        <section
          className="mgrEmptyCard"
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "left",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div className="mgrEmptyTitle" style={{ marginBottom: 4 }}>
              Sales Rep Access {isSalesRep ? "· Enabled" : ""}
            </div>
            <div className="mgrEmptySub">
              {isSalesRep
                ? "This employee can browse & search every project in the Project Showcase."
                : "Grant this employee read-only access to browse all projects in the Project Showcase."}
            </div>
          </div>
          {isSalesRep ? (
            <button className="mgrOutlineBtn" onClick={removeSalesRep}>
              <PersonRemoveOutlinedIcon sx={{ fontSize: 16 }} />
              Remove Sales Rep
            </button>
          ) : (
            <button className="mgrSolidBtn" onClick={makeSalesRep}>
              <AddIcon sx={{ fontSize: 16 }} />
              Make Sales Rep
            </button>
          )}
        </section>
      )}

      {/* Not a manager */}
      {!isManager && (
        <section className="mgrEmptyCard">
          <span className="mgrEmptyIcon">
            <GroupsOutlinedIcon sx={{ fontSize: 28 }} />
          </span>
          <div className="mgrEmptyTitle">This employee is not a manager</div>
          <div className="mgrEmptySub">
            Direct reports, requests and progress reports appear here once they
            lead a team.
          </div>
          {Employee.manager == "no" && isHrOrExec && (
            <button
              className="mgrSolidBtn"
              style={{ marginTop: 6 }}
              onClick={() => promoteToManager(Employee.email, Employee.name)}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Promote to Manager
            </button>
          )}
        </section>
      )}

      {isManager && (
        <>
          {/* Assign bar */}
          <div className="mgrAssignBar">
            <Autocomplete
              className="mgrAssignSelect"
              options={leademployees}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign New Employee"
                  variant="outlined"
                  size="small"
                />
              )}
              onChange={(event, newValue) => setSelectedleadEmployee(newValue)}
            />
            <button className="mgrSolidBtn" onClick={assignEmployeeToManager}>
              <AddIcon sx={{ fontSize: 16 }} />
              Assign
            </button>
          </div>

          {/* Segmented sub-tabs */}
          <div className="mgrSeg">
            <button
              className={`mgrSegBtn ${reportType === "employeesOfManager" ? "mgrSegBtn--active" : ""}`}
              onClick={() => handleReportTypeChange(null, "employeesOfManager")}
            >
              EMPLOYEES
            </button>
            <button
              className={`mgrSegBtn ${reportType === "requests" ? "mgrSegBtn--active" : ""}`}
              onClick={() => handleReportTypeChange(null, "requests")}
            >
              REQUESTS
            </button>
            <button
              className={`mgrSegBtn ${reportType === "progress" ? "mgrSegBtn--active" : ""}`}
              onClick={() => handleReportTypeChange(null, "progress")}
            >
              PROGRESS REPORT
            </button>
            <button
              className={`mgrSegBtn ${reportType === "overtime" ? "mgrSegBtn--active" : ""}`}
              onClick={() => handleReportTypeChange(null, "overtime")}
            >
              PENDING OVERTIME{pendingOt.length ? ` (${pendingOt.length})` : ""}
            </button>
          </div>

          <div className="mgrContent">
            {reportType === "progress" && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  alignItems: "center",
                  marginTop: "16px",
                }}
              >
                <TextField
                  type="text"
                  size="small"
                  label="Employee name"
                  placeholder="Search by name"
                  InputLabelProps={{ shrink: true }}
                  value={progressEmployeeInput}
                  onChange={(e) => setProgressEmployeeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyProgressFilter();
                  }}
                  sx={{ minWidth: 190 }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={progressFromInput}
                  onChange={(e) => setProgressFromInput(e.target.value)}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={progressToInput}
                  onChange={(e) => setProgressToInput(e.target.value)}
                />
                <button className="mgrSolidBtn" onClick={applyProgressFilter}>
                  Apply
                </button>
                {(progressFrom || progressTo || progressEmployee) && (
                  <button className="mgrOutlineBtn" onClick={clearProgressFilter}>
                    Clear
                  </button>
                )}
                <Select
                  value={progressSort}
                  onChange={(e) => {
                    setProgressSort(e.target.value);
                    setProgressPage(1);
                  }}
                  size="small"
                  sx={{ minWidth: 170, marginLeft: "auto" }}
                >
                  <MenuItem value="date-desc">Newest first</MenuItem>
                  <MenuItem value="date-asc">Oldest first</MenuItem>
                  <MenuItem value="name-asc">Name (A → Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z → A)</MenuItem>
                </Select>
              </div>
            )}
            {reportType === "progress" && projectData.length > 0 ? (
              <TableContainer className="reportPanel" style={{ marginTop: "20px" }}>
                <Table className="reportTable" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Overtime</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectData.map((report) => {
                      const statusRaw = (report.status || "").toLowerCase();
                      const statusClass = statusRaw.includes("accept")
                        ? "accepted"
                        : statusRaw.includes("reject")
                        ? "rejected"
                        : statusRaw.includes("pending")
                        ? "pending"
                        : "none";
                      const hasOt =
                        report.overtimeJustification &&
                        report.overtimeJustification.trim().length > 0;
                      const initial = (report.name || "?")
                        .trim()
                        .charAt(0)
                        .toUpperCase();
                      return (
                        <TableRow key={report._id} className="reportRow">
                          <TableCell>
                            <div className="reportEmp">
                              <span className="reportAvatar">{initial}</span>
                              <div className="reportEmpText">
                                <span className="reportName">{report.name}</span>
                                <button
                                  className="reportViewLink"
                                  onClick={() =>
                                    handleOpenDialog(
                                      report.reportDetail,
                                      report.overtimeJustification
                                    )
                                  }
                                >
                                  View report ›
                                </button>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {new Date(report.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="reportDuration">
                            {report.duration ? report.duration : "N/A"}
                          </TableCell>
                          <TableCell>
                            {hasOt ? (
                              <button
                                className="reportOtCell"
                                title={report.overtimeJustification}
                                onClick={() =>
                                  handleOpenDialog(
                                    report.reportDetail,
                                    report.overtimeJustification
                                  )
                                }
                              >
                                <span className="reportChip reportChip--ot">
                                  Overtime
                                </span>
                                <span className="reportOtNote">
                                  {report.overtimeJustification}
                                </span>
                              </button>
                            ) : (
                              <span className="reportDash">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`reportChip reportChip--${statusClass}`}
                            >
                              {report.status ? report.status : "-"}
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            {statusRaw.includes("pending") ? (
                              <div className="reportActions">
                                <button
                                  className="reportActionBtn reportActionBtn--approve"
                                  title="Approve overtime"
                                  onClick={() =>
                                    sendReportStatus(report.checkInId, "accepted")
                                  }
                                >
                                  <CheckIcon fontSize="small" />
                                </button>
                                <button
                                  className="reportActionBtn reportActionBtn--reject"
                                  title="Reject overtime"
                                  onClick={() =>
                                    sendReportStatus(report.checkInId, "rejected")
                                  }
                                >
                                  <CloseIcon fontSize="small" />
                                </button>
                              </div>
                            ) : (
                              <span className="reportDash">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {/* Pagination Component for Progress Report */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "20px",
                    margin: "20px 0",
                  }}
                >
                  <Pagination
                    count={progressTotalPages}
                    page={progressPage}
                    onChange={handleProgressPageChange}
                    color="primary"
                  />
                  <Select
                    value={progressPage}
                    onChange={handleProgressDropdownChange}
                    sx={{ minWidth: 120 }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {Array.from({ length: progressTotalPages }, (_, index) => (
                      <MenuItem key={index + 1} value={index + 1}>
                        Page {index + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </TableContainer>
            ) : reportType === "overtime" ? (
              <TableContainer className="reportPanel" style={{ marginTop: "20px" }}>
                <p className="mgrOtIntro">
                  Days over 9h 25m waiting for your decision (last 62 days). Approved overtime
                  counts toward total hours; rejected or undecided days count as 9h 25m.
                </p>
                {pendingOtError ? (
                  <div className="mgrOtState mgrOtState--error" role="alert">
                    <span>{pendingOtError}</span>
                    <button className="mgrOutlineBtn" onClick={fetchPendingOvertime}>
                      Try again
                    </button>
                  </div>
                ) : pendingOtLoading && pendingOt.length === 0 ? (
                  <div className="mgrOtState">
                    <CircularProgress size={22} />
                  </div>
                ) : pendingOt.length === 0 ? (
                  <div className="mgrNothing">
                    <span className="mgrEmptyIcon">
                      <EventAvailableOutlinedIcon sx={{ fontSize: 26 }} />
                    </span>
                    <div className="mgrEmptyTitle">No overtime waiting</div>
                    <div className="mgrEmptySub">
                      Days your team works beyond 9h 25m will appear here for approval.
                    </div>
                  </div>
                ) : (
                  <Table className="reportTable" sx={{ minWidth: 680 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Check in → out (PKT)</TableCell>
                        <TableCell>Worked</TableCell>
                        <TableCell>Overtime</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingOt.map((r) => {
                        const inMs = Date.parse(r.checkIn);
                        const outMs = Date.parse(r.checkOut);
                        const workedMs = outMs - inMs;
                        const nextDay =
                          String(r.checkOut).slice(0, 10) !== String(r.checkIn).slice(0, 10);
                        const busy = pendingOtBusy === r._id;
                        return (
                          <TableRow key={r._id} className="reportRow">
                            <TableCell>
                              <div className="reportEmp">
                                <span className="reportAvatar">
                                  {(r.name || "?").trim().charAt(0).toUpperCase()}
                                </span>
                                <div className="reportEmpText">
                                  <span className="reportName mgrOtName">{r.name}</span>
                                  {PENDING_SOURCE[r.source] ? (
                                    <span className="mgrOtSource">{PENDING_SOURCE[r.source]}</span>
                                  ) : (
                                    <span className="mgrOtSource mgrOtSource--own">Own check-out</span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="reportDuration" title={fmtDate(r.checkIn)}>
                              {otDay(r.checkIn)}
                            </TableCell>
                            <TableCell className="reportDuration">
                              {fmtTime(r.checkIn)} → {fmtTime(r.checkOut)}
                              {nextDay ? <span className="mgrOtNext"> next day</span> : null}
                            </TableCell>
                            <TableCell className="reportDuration">
                              {fmtDuration(workedMs)}
                            </TableCell>
                            <TableCell className="reportDuration">
                              +{fmtDuration(Math.max(0, workedMs - OVERTIME_GRACE_MS))}
                            </TableCell>
                            <TableCell align="right">
                              <div className="reportActions">
                                <button
                                  className="reportActionBtn reportActionBtn--approve"
                                  title="Approve overtime"
                                  aria-label={`Approve overtime for ${r.name} on ${fmtDate(r.checkIn)}`}
                                  disabled={busy}
                                  onClick={() => decidePendingOvertime(r, "accepted")}
                                >
                                  <CheckIcon fontSize="small" />
                                </button>
                                <button
                                  className="reportActionBtn reportActionBtn--reject"
                                  title="Reject overtime"
                                  aria-label={`Reject overtime for ${r.name} on ${fmtDate(r.checkIn)}`}
                                  disabled={busy}
                                  onClick={() => decidePendingOvertime(r, "rejected")}
                                >
                                  <CloseIcon fontSize="small" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            ) : reportType === "requests" ? (
              <Leaves
                user={{
                  roleM:
                    user.role === "HR" || user.role === "Executive"
                      ? "HR"
                      : "Manager",
                  leads: unleadEmployees,
                }}
              />
            ) : reportType === "employeesOfManager" &&
              unleadEmployees?.length > 0 ? (
              <section className="mgrPanel">
                <header className="mgrPanelHead">
                  <h3 className="mgrPanelTitle">Team Members</h3>
                  <span className="mgrTeamBadge">
                    {unleadEmployees.length} total
                  </span>
                </header>
                {unleadEmployees.map((e) => (
                  <div
                    className="mgrEmpRow"
                    key={e._id}
                    onClick={() => openEmployeeDetails(e)}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="mgrEmpAvatar">{initials(e.name)}</span>
                    <div className="mgrEmpInfo">
                      <div className="mgrEmpName">{e.name}</div>
                      <div className="mgrEmpEmail">{e.email}</div>
                    </div>
                    <KeyboardArrowRightIcon className="mgrChevron" />
                  </div>
                ))}
              </section>
            ) : (
              <div className="mgrNothing">
                <span className="mgrEmptyIcon">
                  <GroupsOutlinedIcon sx={{ fontSize: 26 }} />
                </span>
                <div className="mgrEmptyTitle">Nothing to show yet</div>
                <div className="mgrEmptySub">
                  Items will appear here once available.
                </div>
              </div>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
              <DialogTitle>Report Detail</DialogTitle>
              <DialogContent>
                <DialogContentText style={{ whiteSpace: "pre-wrap" }}>
                  {selectedReportDetail}
                </DialogContentText>
                {selectedOvertimeNote &&
                  selectedOvertimeNote.trim().length > 0 && (
                    <>
                      <div className="reportDlgLabel">Overtime Justification</div>
                      <DialogContentText style={{ whiteSpace: "pre-wrap" }}>
                        {selectedOvertimeNote}
                      </DialogContentText>
                    </>
                  )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={openUnassignDialog} onClose={handleCloseUnassignDialog}>
              <DialogTitle>Unassign Employee</DialogTitle>
              <DialogContent>
                <Autocomplete
                  options={unleadEmployees}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unassign Employee"
                      variant="outlined"
                    />
                  )}
                  onChange={(event, newValue) =>
                    setSelectedUnleadEmployee(newValue)
                  }
                  sx={{ width: "100%", marginTop: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={unassignEmployee}
                  style={{ marginTop: 16 }}
                >
                  Unassign
                </Button>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseUnassignDialog}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Team member personal-info popup */}
            <Dialog
              open={empDialogOpen}
              onClose={() => setEmpDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{selectedEmp?.name || "Employee"}</DialogTitle>
              <DialogContent dividers>
                {empLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                    <CircularProgress size={26} />
                  </div>
                ) : (
                  <div className="mgrEmpDetails">
                    {employeeDetailFields(selectedEmp).map(([label, value]) => (
                      <div className="mgrEmpDetailRow" key={label}>
                        <span className="mgrEmpDetailLabel">{label}</span>
                        <span className="mgrEmpDetailValue">{value}</span>
                      </div>
                    ))}
                    {employeeDetailFields(selectedEmp).length === 0 && (
                      <p className="mgrEmpDetailEmpty">
                        No additional details available for this employee.
                      </p>
                    )}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEmpDialogOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </div>

          {isHrOrExec && (
            <div className="mgrRemoveBlock">
              <hr />
              <Button
                variant="outlined"
                color="error"
                onClick={() => setOpenD(true)}
              >
                Remove from Manager
              </Button>

              <Dialog open={openD} onClose={() => setOpenD(false)}>
                <DialogTitle>
                  Are you sure you want to remove him/her from Manager?
                </DialogTitle>
                <DialogActions>
                  <Button onClick={() => setOpenD(false)} color="primary">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      removeFromManager(Employee.email, Employee.name);
                      setOpenD(false);
                    }}
                    color="error"
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          )}
        </>
      )}

      {/* MUI Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManagerInfo;
